/**
 * Frontend types for the new DXF/DWG viewer scene contract.
 *
 * The payload is immutable: mapping and other overlays sit on top of a scene
 * rather than mutating it. `readonly` is the compiler contract for that.
 */

/** Scene schema version the frontend is written against. Bumped by the backend on breaking changes. */
export const DXF_SCENE_SCHEMA_VERSION = 2;

/** A 2D point: `[x, y]`. */
export type DxfPoint = readonly [number, number];

/**
 * A resolved line style — colour, lineweight, and dash pattern. Shared by the
 * entries in the scene's `styles` table and by each layer's default `style`.
 */
export type DxfStyle = {
  /** Hex colour string, e.g. `"#ff0000"`. */
  readonly rgb: string;
  /** Lineweight in millimetres. */
  readonly lineweight: number;
  /**
   * Simplified linetype pattern in drawing units: a positive `[line, gap, line,
   * gap, ...]` sequence (`0` = a dot), empty for a continuous line. The backend
   * resolves this from the file's own linetype table.
   */
  readonly dash: readonly number[];
};

/** A DXF layer and its resolved default style. Layer colour comes from `style.rgb`. */
export type DxfLayer = {
  readonly name: string;
  readonly style: DxfStyle;
};

/**
 * Known source DXF entity type tags on scene primitives. Not exhaustive — ezdxf
 * can emit others — but these are the values the viewer and PMI logic reference.
 */
export const DXF_ENTITY_TYPE = {
  LINE: 'LINE',
  CIRCLE: 'CIRCLE',
  ARC: 'ARC',
  ELLIPSE: 'ELLIPSE',
  SPLINE: 'SPLINE',
  LWPOLYLINE: 'LWPOLYLINE',
  POLYLINE: 'POLYLINE',
  POINT: 'POINT',
  DIMENSION: 'DIMENSION',
  LEADER: 'LEADER',
  MULTILEADER: 'MULTILEADER',
  TOLERANCE: 'TOLERANCE',
  HATCH: 'HATCH',
  INSERT: 'INSERT',
  TEXT: 'TEXT',
  MTEXT: 'MTEXT',
} as const;

/** Source entity types whose geometry is reconstructed dimension annotation (PMI). */
export const DXF_PMI_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.DIMENSION,
  DXF_ENTITY_TYPE.LEADER,
  DXF_ENTITY_TYPE.MULTILEADER,
  DXF_ENTITY_TYPE.TOLERANCE,
]);

export type DxfEntity = {
  /** DXF handle, or a synthetic handle for exploded block children. */
  readonly h: string;
  /**
   * Block-definition handle chain for geometry exploded from an INSERT
   * (outermost first). `h` is shared by every primitive of one INSERT, so
   * `[h, ...src]` is the stable unique address of geometry *within* a block
   * instance. Absent for geometry drawn directly in modelspace.
   */
  readonly src?: readonly string[];
  /** Entity type. one of {@link DXF_ENTITY_TYPE} but that enum does not represent an exhasutive set of possible values. */
  readonly t: string;
  /** Index into `DxfScene.layers`. */
  readonly layer: number;
  /** Index into `DxfScene.styles`. */
  readonly style: number;
  /** Polyline vertices, or the exterior ring when `fill` is set (at least one). */
  readonly pts: readonly DxfPoint[];
  /** `true` when `pts` describes a filled polygon (hatch, glyph fill) rather than a stroke. */
  readonly fill?: boolean;
  /** Inner rings cut out of the fill (glyph counters, hatch islands). */
  readonly holes?: readonly (readonly DxfPoint[])[];
};

/**
 * A raw text string + its oriented bounding box, for the frontend's fitted,
 * selectable text layer. Text is not rendered as glyph geometry; the frontend
 * draws the real string as HTML fitted into this box.
 */
export type DxfText = {
  /** Source text entity handle. */
  readonly h: string;
  /** Block-definition handle chain for text living inside a block (see `DxfEntity.src`). */
  readonly src?: readonly string[];
  /** Plain (formatting-stripped) string, with special chars decoded to Unicode. */
  readonly s: string;
  /** Index into `DxfScene.layers` (so layer toggles hide text too). */
  readonly layer: number;
  /** Resolved text colour (hex), matching the glyph colour it replaces. */
  readonly rgb: string;
  /** WCS bottom-left corner of the oriented text box. */
  readonly pos: DxfPoint;
  /** Box width in the text's local (unrotated) frame, in drawing units. */
  readonly width: number;
  /** Box height in the text's local (unrotated) frame, in drawing units. */
  readonly height: number;
  /** Single-line (cap) height in drawing units, used as the font size. */
  readonly charHeight: number;
  /** DXF horizontal scale (TEXT group 41); 1.0 = unscaled. */
  readonly widthFactor: number;
  /**
   * Alignment as a fraction of the box: `[ax, ay]`, where `(0,0)` is
   * bottom-left, `(0.5,0.5)` center and `(1,1)` top-right.
   */
  readonly anchor: DxfPoint;
  /** Rotation in degrees. */
  readonly rotation: number;
  /** `true` for MTEXT with line breaks / column wrapping (reflowed, not width-fitted). */
  readonly multiline: boolean;
};

/** The full viewer scene payload fetched from the presigned scene-cache URL. */
export type DxfScene = {
  readonly schemaVersion: number;
  /** Owning `Drawing` PK; `null` for a scene built outside a persisted row. */
  readonly drawingId: number | null;
  /**
   * Not emitted by the scene-cache blob. Measurement units come from
   * `PartFile.dxfSceneUnits` (`Drawing.resolved_units`) on the PartFile response.
   */
  readonly units?: number;
  /** World-space bounds `[minX, minY, maxX, maxY]`, or `null` for an empty scene. */
  readonly bbox: readonly [number, number, number, number] | null;
  readonly layers: readonly DxfLayer[];
  readonly styles: readonly DxfStyle[];
  readonly entities: readonly DxfEntity[];
  readonly texts: readonly DxfText[];
};
